import React, {
  useEffect,
  useState,
  useCallback,
  useMemo,
  lazy,
  Suspense,
} from "react";

import { Button, Col, Row } from "react-bootstrap";
import "./GNSSOverview.css";

import { CardComponent, InfrastructureStatus } from "components";

const MapContainer = lazy(() =>
  import("components").then((module) => ({ default: module.MapContainer })),
);
const RealtimeDotsGraph = lazy(() =>
  import("components").then((module) => ({
    default: module.RealtimeDotsGraph,
  })),
);
const GNSSStatusTable = lazy(() =>
  import("components").then((module) => ({ default: module.GNSSStatusTable })),
);

import { COLORS, StationStatus } from "utils";

import { Link } from "react-router-dom";

import { useGnssApi, useAuth, useWebSocket } from "utils/hooks";

import { useFilter } from "../../utils/hooks";
export const GNSSOverview = () => {
  const api = useGnssApi();
  const {selected_tenant_id} = useAuth();

  const { groupFilter } = useFilter();
  var mapDimensions = [950, 600];

  var countries = [{ name: "RO" }, { name: "AU" }];

  const [gnssStationsDetails, setGnssStationsDetails] = useState([]);

  const [tenantCountry, setTenantCountry] = useState(countries[0]);

  const [interferenceDetails, setInterferenceDetails] = useState([]);
  const [performanceTable, setPerformanceTable] = useState({});
  const [gnssUnitsUp, setGnssUnitsUp] = useState(0);
  const [gnssNoData, setGnssNoData] = useState(0);
  const [isConnected, subscribe, unsubscribe] = useWebSocket();
  const channelName = `${selected_tenant_id}-general-interferences`;

  useEffect(() => {
    if (isConnected === true) {
      // Subscribe to the desired WebSocket channel
      subscribe(channelName, handleGnssReceiveCallbackMessage);
    }
    // Cleanup subscription on component unmount
    return () => {
      unsubscribe(channelName);
    };
  }, [isConnected, subscribe, unsubscribe]);

  const gnssMarkers = useMemo(() => {
    return gnssStationsDetails?.map((resp) => ({
      position: [resp.location.lon, resp.location.lat],
      name: resp.name,
      topic: resp.identifier,
      type: "gnss",
      stationType: resp.type,
      status: -1,
      network:
        resp.groupNames.length < 2
          ? resp.groupNames[0]
          : "Multiple groups",
    }));
  }, [gnssStationsDetails]);

  var handleGnssReceiveCallbackMessage = useCallback(
    (stompMessage) => {
      const payload = JSON.parse(stompMessage.body);

      if (groupFilter.includes(payload.stationId)) return;

      setInterferenceDetails(payload.interferenceEvents.interferenceDetails);

      setPerformanceTable((prevState) => {
        const newState = { ...prevState };

        // Keep only the last 100 entries
        const keys = Object.keys(newState);
        if (keys.length >= 100 && !newState[payload.stationId]) {
          delete newState[keys[0]]; // Remove the oldest key (basic LRU-like)
        }

        const values = Object.values(payload.performance).flatMap((obj) =>
          Object.values(obj),
        );

        const allNotNull = values.every((v) => v !== null);
        const atLeastOneNotNull = values.some((v) => v !== null);

        if (
          payload.stationId &&
          payload.performance &&
          (allNotNull || atLeastOneNotNull)
        ) {
          newState[payload.stationId] = payload.performance;
        }

        return newState;
      });
    },
    [groupFilter],
  );

  const getAllGnssStationsDetails = useCallback(async () => {
    try {
      let response = await api.getAllStationsDetails();

      response = response.data;

      response = response.filter((station) =>
        station.groupNames.some(
          (groupName) => !groupFilter.includes(groupName),
        ),
      );

      setGnssStationsDetails(response);
      const initialPerformanceTable = {};
      response
        ?.map((obj) => obj.identifier)
        ?.forEach((key) => {
          initialPerformanceTable[key] = {};
        });
      setPerformanceTable(initialPerformanceTable);

      return response;
    } catch (e) {
      // alert(e);
      //throw e;
    }
  }, [api, groupFilter]);

  useEffect(() => {
    getAllGnssStationsDetails();
  }, []);

  return (
    <>
      <Row className="m-3">
        <Row>
          <Col className="text-center">
            {tenantCountry && (
              <Suspense fallback={<div>Loading map...</div>}>
                <MapContainer
                  hasIntererence={true}
                  width={mapDimensions[0]}
                  height={mapDimensions[1]}
                  scale={mapDimensions[0]}
                  type="overall"
                  markersGnss={gnssMarkers}
                  country={tenantCountry.name}
                  onCountryChanged={(countryName) => {
                    setTenantCountry(
                      countries.find((c) => c.name === countryName),
                    );
                  }}
                  onGnssStatusChanged={(markers, topic) => {
                    const { last, current } = markers;
                    const transitions = [
                      {
                        condition:
                          (last === StationStatus.state.CONNECTING ||  last === StationStatus.state.DISCONNECTED) && 
                          current === StationStatus.state.AVAILABLE_DATA,
                        action: () =>
                          setGnssUnitsUp((prevUnits) => prevUnits + 1),
                      },
                      {
                        condition:
                          last === StationStatus.state.DATA_UNAVAILABLE &&
                          current === StationStatus.state.AVAILABLE_DATA,
                        action: () => {
                          setGnssUnitsUp((prevUnits) => prevUnits + 1);
                          setGnssNoData(
                            (prevUnits) => prevUnits >= 0 && prevUnits - 1,
                          );
                        },
                      },
                      {
                        condition:
                          last === StationStatus.state.AVAILABLE_DATA &&
                          current === StationStatus.state.DATA_UNAVAILABLE,
                        action: () => {
                          setGnssUnitsUp(
                            (prevUnits) => prevUnits >= 0 && prevUnits - 1,
                          );
                          setGnssNoData((prevUnits) => prevUnits + 1);
                        },
                      },
                      {
                        condition:
                          (last === StationStatus.state.CONNECTING ||  last === StationStatus.state.DISCONNECTED) &&
                          current === StationStatus.state.DATA_UNAVAILABLE,
                        action: () =>
                          setGnssNoData((prevUnits) => prevUnits + 1),
                      },
                      {
                        condition:
                          last === StationStatus.state.AVAILABLE_DATA &&
                          current === StationStatus.state.DISCONNECTED,
                        action: () =>
                          setGnssUnitsUp(
                            (prevUnits) => prevUnits >= 0 && prevUnits - 1,
                          ),
                      },
                      {
                        condition:
                          last === StationStatus.state.DATA_UNAVAILABLE &&
                          current === StationStatus.state.DISCONNECTED,
                        action: () =>
                          setGnssNoData(
                            (prevUnits) => prevUnits >= 0 && prevUnits - 1,
                          ),
                      },
                     
                    ];

                    transitions.forEach(({ condition, action }) => {
                      if (condition) {
                        action();
                      }
                    });
                  }}
                />
              </Suspense>
            )}
          </Col>
          <Col>
            <Row>
              <Col style={{ minHeight: 600, minWidth: 550 }}>
                <InfrastructureStatus
                  gnssUpUnits={gnssUnitsUp}
                  gnssDownUnits={
                    gnssStationsDetails?.length -
                    gnssNoData -
                    gnssUnitsUp
                  }
                  gnssNoDataUnits={gnssNoData}
                  gnssTotalUnits={gnssStationsDetails?.length}
                />
              </Col>
            </Row>
          </Col>
        </Row>
        <Row>
          <Col
            xxl={6}
            xl={12}
            lg={12}
            md={12}
            sm={12}
            xs={12}
            style={{ minWidth: 550 }}
          >
            <CardComponent title={"Interference events"}>
              <Suspense fallback={<div>Loading realtime graph...</div>}>
                <RealtimeDotsGraph
                  width={700}
                  height={300}
                  interferences={interferenceDetails?.map((sample) => ({
                interferenceFrequency: sample.interferenceFrequency / 1e6,
              }))}
                  defaultOptiopns={{
                    title: "Interferences",

                    yMinValue: 1000,
                    yMaxValue: 1800,
                    yTicks: 6,
                    thresholdType: 0,
                    legendValues: ["L1", "L5", "E6"],
                    thresholds: [
                      { value: 1188, label: "1188 MHz", color: "red" },
                      { value: 1231, label: "1231 MHz", color: "green" },
                      { value: 1275, label: "1275 MHz", color: "blue" },
                      { value: 1584, label: "1584 MHz", color: "yellow" },
                    ],
                  }}
                  dataset={null}
                />
              </Suspense>
            </CardComponent>
          </Col>
          <Col xxl={6} xl={12} lg={12} md={12} style={{ minWidth: 550 }}>
            <CardComponent title={"GNSS core constellations status"}>
              <Suspense fallback={<div>Loading status tables...</div>}>
                {Object.keys(performanceTable ?? [])
                  .filter(
                    (key) => Object.keys(performanceTable[key]).length !== 0,
                  ) // Filter out empty keys
                  .slice(0, 4)
                  .map(
                    (key, index, array) =>
                      index % 2 === 0 && (
                        <Row className="mt-0" key={index}>
                          <Col>
                            <GNSSStatusTable
                              title={gnssStationsDetails
                                ?.filter(
                                  (stationDetails) =>
                                    stationDetails.identifier === key,
                                )
                                .map((station) => station.name)}
                              performanceTableData={performanceTable[key]}
                            />
                          </Col>
                          {index + 1 < array.length && (
                            <Col>
                              <GNSSStatusTable
                                title={gnssStationsDetails
                                  ?.filter(
                                    (stationDetails) =>
                                      stationDetails.identifier ===
                                      array[index + 1],
                                  )
                                  .map((station) => station.name)}
                                // title={array[index + 1]}
                                performanceTableData={
                                  performanceTable[array[index + 1]]
                                }
                              />
                            </Col>
                          )}
                        </Row>
                      ),
                  )}
              </Suspense>
              <Row>
                <Col className="text-center">
                  <Link to="/gnss/spectrum">
                    <Button
                      variant="link"
                      style={{ color: COLORS.selectedColorTheme }}
                    >
                      View more details...
                    </Button>
                  </Link>
                </Col>
              </Row>
            </CardComponent>
          </Col>
        </Row>
      </Row>
    </>
  );
};
